/**
 * KeyDatesSection — canonical "Key Dates" outline section under a milestone (docs/KEY_DATES.md).
 *
 * Calendar flags for the crew — "Scheduled Hearing Date," a client move-in target — NOT
 * milestones with work attached, not gated, not scheduled by the nightly scheduler. Same
 * non-zoomable, React-only shape as WorkingNotesSection.jsx (the precedent this mirrors):
 * own collapse state, load-on-expand, add/edit/delete from ⋯ menus.
 *
 * Key dates live at the PROJECT level (a key date can be linked to more than one milestone —
 * Public Hearing Preparation and Public Hearing share one "Scheduled Hearing Date" by design),
 * so the cache here is keyed by PROJECT, not by milestone: `ctx.keyDatesCache[projectId]` holds
 * every key date for the project once, and every milestone's KeyDatesSection filters that same
 * array down to the ones linked to it. Editing a key date from any milestone mutates the one
 * shared array in place, so every other milestone showing it re-renders with the new value on
 * the very next `outline-render` — no per-milestone propagation needed.
 */

import { useEffect, useRef, useState } from 'react'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import { getProjectKeyDates, unlinkKeyDateFromMilestone, deleteKeyDate } from '../services/keyDates.js'
import { confirmDialog, alertDialog } from './Dialog.jsx'
import AddKeyDateModal from './AddKeyDateModal.jsx'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
function fmtDate(d) {
  if (!d) return null
  const p = d.split('-')
  return MONTHS[parseInt(p[1], 10) - 1] + ' ' + parseInt(p[2], 10) + ', ' + p[0]
}

function currentMemberInfo() {
  return {
    memberId: ctx.currentMember?.id || null,
    memberName: ctx.currentMember?.fields?.['Name'] || ctx.currentMember?.fields?.['Member Name'] || null,
  }
}

function LoadingRow() {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)' }}>Loading…</span>
        </div>
      </div>
    </div>
  )
}

function EmptyRow() {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No key dates on this milestone.</span>
        </div>
      </div>
    </div>
  )
}

function KeyDateRow({ kd, msId, projectId }) {
  const menu = useActionMenu()
  const [editing, setEditing] = useState(false)
  const [applying, setApplying] = useState(false)
  const otherCount = (kd.milestoneIds || []).length - 1
  const dateStr = fmtDate(kd.date)

  function replaceInCache(updated) {
    const arr = ctx.keyDatesCache?.[projectId]
    if (Array.isArray(arr)) {
      const idx = arr.findIndex(k => k.id === updated.id)
      if (idx !== -1) arr[idx] = updated
    }
    window.dispatchEvent(new Event('outline-render'))
  }

  async function removeFromThisMilestone() {
    if (!(await confirmDialog({ title: 'Remove key date?', message: 'Remove "' + kd.label + '" from this milestone? It stays on any other milestone it\'s linked to.', confirmLabel: 'Remove' }))) return
    try {
      const res = await unlinkKeyDateFromMilestone(kd.id, msId)
      if (res.deleted) {
        const arr = ctx.keyDatesCache?.[projectId]
        if (Array.isArray(arr)) { const idx = arr.findIndex(k => k.id === kd.id); if (idx !== -1) arr.splice(idx, 1) }
        window.dispatchEvent(new Event('outline-render'))
      } else if (res.keyDate) {
        replaceInCache(res.keyDate)
      }
    } catch (e) { alertDialog({ title: 'Failed', message: e.message || String(e) }) }
  }

  async function deleteEverywhere() {
    if (!(await confirmDialog({ title: 'Delete key date?', message: 'Delete "' + kd.label + '" entirely? This removes it from every milestone it\'s linked to. This cannot be undone.', confirmLabel: 'Delete', danger: true }))) return
    try {
      await deleteKeyDate(kd.id)
      const arr = ctx.keyDatesCache?.[projectId]
      if (Array.isArray(arr)) { const idx = arr.findIndex(k => k.id === kd.id); if (idx !== -1) arr.splice(idx, 1) }
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) { alertDialog({ title: 'Delete failed', message: e.message || String(e) }) }
  }

  return (
    <div className="wf-node wf-node--item" data-key={'key-date:' + kd.id}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item"></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text">{kd.label}</span>
          <span className="wf-meta" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {dateStr
              ? <span className="wf-date-btn" style={{ pointerEvents: 'none' }}>{dateStr}</span>
              : <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10.5, color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>not scheduled</span>}
            {otherCount > 0 && (
              <span title="Also shown on other milestones" style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)' }}>
                shared · +{otherCount}
              </span>
            )}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => setEditing(true)}>Edit</ActionMenuItem>
        <ActionMenuItem onClick={() => setApplying(true)}>Apply to more milestones…</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={removeFromThisMilestone}>Remove from this milestone</ActionMenuItem>
        <ActionMenuItem onClick={deleteEverywhere} danger>Delete everywhere</ActionMenuItem>
      </ActionMenu>
      {editing && (
        <AddKeyDateModal
          mode="edit" projectId={projectId} existing={kd} {...currentMemberInfo()}
          onClose={() => setEditing(false)}
          onSaved={(updated) => { setEditing(false); replaceInCache(updated) }}
        />
      )}
      {applying && (
        <AddKeyDateModal
          mode="apply" projectId={projectId} existing={kd} {...currentMemberInfo()}
          onClose={() => setApplying(false)}
          onSaved={(updated) => { setApplying(false); replaceInCache(updated) }}
        />
      )}
    </div>
  )
}

/**
 * @param {string} msId — the Milestone's Airtable record id
 * @param {string} projectId — the owning Project's Airtable record id
 */
export default function KeyDatesSection({ msId, projectId }) {
  const key = 'keydates:' + msId
  const isCollapsed = wf.collapsed[key] !== false
  const allForProject = ctx.keyDatesCache?.[projectId] // undefined=not started, null=in-flight, []=loaded
  const menu = useActionMenu()
  const loadedRef = useRef(false)
  const [adding, setAdding] = useState(false)

  function load() {
    if (!ctx.keyDatesCache) ctx.keyDatesCache = {}
    ctx.keyDatesCache[projectId] = null
    getProjectKeyDates(projectId).then(list => {
      if (!ctx.keyDatesCache) ctx.keyDatesCache = {}
      ctx.keyDatesCache[projectId] = list
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {
      if (!ctx.keyDatesCache) ctx.keyDatesCache = {}
      ctx.keyDatesCache[projectId] = []
      window.dispatchEvent(new Event('outline-render'))
    })
  }

  useEffect(() => {
    if (isCollapsed || !projectId) return
    if (loadedRef.current) return
    loadedRef.current = true
    load()
  }, [isCollapsed]) // eslint-disable-line react-hooks/exhaustive-deps

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  const items = Array.isArray(allForProject) ? allForProject.filter(kd => (kd.milestoneIds || []).includes(msId)) : null

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Key Dates"></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Key Dates</span>
          {Array.isArray(items) && <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{items.length}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => setAdding(true)}>Add key date</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          {items === null && <LoadingRow />}
          {Array.isArray(items) && items.length === 0 && <EmptyRow />}
          {Array.isArray(items) && items.map(kd => (
            <KeyDateRow key={kd.id} kd={kd} msId={msId} projectId={projectId} />
          ))}
        </div>
      )}
      {adding && (
        <AddKeyDateModal
          mode="create" projectId={projectId} currentMilestoneId={msId} {...currentMemberInfo()}
          onClose={() => setAdding(false)}
          onSaved={(kd) => {
            setAdding(false)
            if (!ctx.keyDatesCache) ctx.keyDatesCache = {}
            const arr = ctx.keyDatesCache[projectId]
            ctx.keyDatesCache[projectId] = Array.isArray(arr) ? [...arr, kd] : [kd]
            loadedRef.current = true
            window.dispatchEvent(new Event('outline-render'))
          }}
        />
      )}
    </div>
  )
}
